import { useEffect, useRef } from "react";

/**
 * useDebounceWithMaxDelay
 *
 * Debounces a callback, but guarantees that the callback will run
 * no later than `maxDelay` after the first change in a burst.
 *
 * Why this exists:
 * - Normal debounce waits for "quiet time" before firing.
 * - But if changes keep coming in rapidly, the callback may never run.
 * - This hook ensures the callback *always* fires within `maxDelay`,
 *   even if the user or system keeps triggering updates.
 *
 * Example use cases:
 * - Autosaving text input (save after 500ms idle, but at least every 5s)
 * - Batched analytics/metrics reporting
 * - Expensive computations that must eventually run
 *
 * Parameters:
 * - callback  → function to run when debounce fires
 * - deps      → dependency array that triggers the debounce
 * - delay     → normal debounce delay (ms)
 * - maxDelay  → maximum allowed delay before forced execution (ms)
 *
 * Behavior:
 * - On first change, we record a "burst start" timestamp.
 * - Each subsequent change resets the debounce timer.
 * - If the time since the burst start exceeds `maxDelay`,
 *   the callback fires immediately (forced execution).
 * - After execution (forced or normal), the burst resets.
 */
export function useDebounceWithMaxDelay({callback, delay, maxDelay, dependencies = []}) {

  if (maxDelay <= delay) {
    throw new Error("Max delay must be greater than delay.");
  }

  // Delay settings expected to be constant
  const delay_ = useRef(delay);
  const maxDelay_ = useRef(maxDelay);

  const firstChange = useRef(0);
  const timeout = useRef(null);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const now = Date.now();
    const delay = delay_.current;
    const maxDelay = maxDelay_.current;

    const fire = () => {
      callback();
      firstChange.current = 0;
    };

    const isFirst = firstChange.current === 0;
    if (isFirst) {
      firstChange.current = now;
    }

    clearTimeout(timeout.current);

    const timeSinceFirst = now - firstChange.current;

    if (isFirst || timeSinceFirst < maxDelay) {
      timeout.current = setTimeout(fire, delay);
    } else {
      fire();
    }

    return () => clearTimeout(timeout.current);
  }, [...dependencies, callback]);
  /* eslint-enable react-hooks/exhaustive-deps */
}

